import { sampleSpecColumnKeys, zSampleSpecRow } from '@resistapp/common/sample-sheet';
import { zSampleSubType, zSampleType } from '@resistapp/common/sampling-point-types';
import { z } from 'zod';
import { carbapenem, L1Targets, zL2Targets } from './assays';
import { zFeature, zLabFeature } from './features';
import { defaultGenePanelKeys, zGenePanelConfiguration } from './gene-panels';
import {
  zLabDnaStepSummaryResponse,
  zLabWorkbookServices,
  zMarkLabDnaStepReadyRequest,
  zSaveLabDnaStepRequest,
  type LabDnaStepSummaryResponse,
  type MarkLabDnaStepReadyRequest,
  type SaveLabDnaStepRequest,
} from './lab-dna-step';
import { SUPPRESSED_VOLUME_MODES } from './normalisation-utils';
import { passwordRegExp, passwordRequirementsMessage } from './password';
import {
  doesQaLabelTypeAllowResultApplication,
  getQaInvalidResultApplicationMessage,
  getQaInvalidScopeMessage,
  isQaLabelScopeAllowedForType,
  isQaLabelTmInRange,
  QA_LABEL_TM_MAX,
  QA_LABEL_TM_MIN,
} from './qa-label-utils';
import { isEuropeanUnionCountry } from './shipment-countries';
import {
  AccessLevel,
  Continent,
  GenePanelState,
  HeatmapType,
  NormalisationMode,
  OrganisationType,
  ProcessFlowKind,
  ProcessKind,
  QaLabelType,
  SampleStatus,
  ShipmentStatus,
  XAxisMode,
  zAccessLevel,
  zCoordinates,
  zCtTableColumns,
  zDispenserPrefix,
  zLabWorkbook,
  zLabWorkbookStatus,
  zMetricMode,
  zOrganisationType,
  zOrganisationUserRole,
  zQaLabel,
  zQaLabelScope,
  zQaLabelScopeEntry,
  zQaLabelType,
  zReferenceMetric,
  zSamplingPoint,
  zShipmentStatus,
} from './types';

export { zOrganisationUserRole };

const zBooleanish = z
  .union([z.boolean(), z.literal('true'), z.literal('false'), z.literal('1'), z.literal('0')])
  .transform(value => value === true || value === 'true' || value === '1');

export const zPostOrganisationUserRequest = z
  .object({
    role: zOrganisationUserRole.optional(),
  })
  .default({});
export type PostOrganisationUserRequest = z.infer<typeof zPostOrganisationUserRequest>;

export const zPostOrganisationUserByEmailRequest = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  role: zOrganisationUserRole.optional(),
});
export type PostOrganisationUserByEmailRequest = z.infer<typeof zPostOrganisationUserByEmailRequest>;

export const zPostOrganisationUserByEmailResponse = z.object({
  userId: z.number().int().positive(),
  email: z.string().email(),
  added: z.boolean(),
  alreadyMember: z.boolean(),
});
export type PostOrganisationUserByEmailResponse = z.infer<typeof zPostOrganisationUserByEmailResponse>;

export const zPostOrganisationInvitesRequest = z.object({
  emails: z
    .array(z.string().email({ message: 'Invalid email address' }))
    .min(1, { message: 'At least one email is required' }),
});
export type PostOrganisationInvitesRequest = z.infer<typeof zPostOrganisationInvitesRequest>;

export const zEmailDeliveryStatus = z.enum([
  'NOT_SENT',
  'SEND_FAILED',
  'SENT',
  'PROCESSED',
  'DELIVERED',
  'DEFERRED',
  'BOUNCE',
  'DROPPED',
]);
export type EmailDeliveryStatus = z.infer<typeof zEmailDeliveryStatus>;

export const zOrganisationInviteDeliveryStatus = zEmailDeliveryStatus;
export type OrganisationInviteDeliveryStatus = EmailDeliveryStatus;

export const zEmailPurpose = z.enum([
  'ORGANISATION_INVITE',
  'USER_VERIFICATION',
  'PASSWORD_RESET',
  'SHIPMENT_REQUEST_NOTIFICATION',
  'SHIPMENT_DOCUMENTS',
]);
export type EmailPurpose = z.infer<typeof zEmailPurpose>;

export const zEmailDeliverySummary = z.object({
  id: z.number().int().positive(),
  purpose: zEmailPurpose,
  subject: z.string(),
  sgMessageId: z.string().nullable(),
  deliveryStatus: zEmailDeliveryStatus,
  deliveryStatusReason: z.string().nullable(),
  deliveryStatusAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
});
export type EmailDeliverySummary = z.infer<typeof zEmailDeliverySummary>;

export const zOrganisationInviteSendResult = z.object({
  inviteId: z.number().int().positive(),
  email: z.string().email(),
  sent: z.boolean(),
  error: z.string().nullable().optional(),
  sgMessageId: z.string().nullable(),
  deliveryStatus: zOrganisationInviteDeliveryStatus,
  deliveryStatusReason: z.string().nullable(),
  deliveryStatusAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  expiresAt: z.string(),
});
export type OrganisationInviteSendResult = z.infer<typeof zOrganisationInviteSendResult>;

export const zPostOrganisationInvitesResponse = z.object({
  results: z.array(zOrganisationInviteSendResult),
});
export type PostOrganisationInvitesResponse = z.infer<typeof zPostOrganisationInvitesResponse>;

export const zOrganisationMemberUser = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  role: zOrganisationUserRole,
});
export type OrganisationMemberUser = z.infer<typeof zOrganisationMemberUser>;

export const zOrganisationPendingInvite = z.object({
  id: z.number().int().positive(),
  email: z.string().email(),
  sgMessageId: z.string().nullable(),
  deliveryStatus: zOrganisationInviteDeliveryStatus,
  deliveryStatusReason: z.string().nullable(),
  deliveryStatusAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  expiresAt: z.string(),
});
export type OrganisationPendingInvite = z.infer<typeof zOrganisationPendingInvite>;

export const zOrganisationMembersResponse = z.object({
  users: z.array(zOrganisationMemberUser),
  invites: z.array(zOrganisationPendingInvite),
});
export type OrganisationMembersResponse = z.infer<typeof zOrganisationMembersResponse>;

export const zInviteLookupResponse = z.object({
  email: z.string().email(),
  organisationName: z.string(),
});
export type InviteLookupResponse = z.infer<typeof zInviteLookupResponse>;

export const zLoginRequest = z.object({
  email: z.string(),
  password: z.string(),
});
export type LoginRequest = z.infer<typeof zLoginRequest>;

export const zAnalyticsEventActionType = z.enum(['landing', 'navigation', 'query', 'download', 'login']);
export type AnalyticsEventActionType = z.infer<typeof zAnalyticsEventActionType>;

export const zAnalyticsEventMetadata = z.record(z.string(), z.unknown());
export type AnalyticsEventMetadata = z.infer<typeof zAnalyticsEventMetadata>;

export const zAnalyticsEventRequestItem = z.object({
  occurredAt: z.string().min(1),
  sessionId: z.string().min(1).max(128),
  path: z.string().min(1).max(2048),
  actionType: zAnalyticsEventActionType,
  query: z.string().max(4096).optional(),
  metadata: zAnalyticsEventMetadata.optional(),
});
export type AnalyticsEventRequestItem = z.infer<typeof zAnalyticsEventRequestItem>;

export const zPostAnalyticsEventsRequest = z.object({
  events: z.array(zAnalyticsEventRequestItem).min(1).max(100),
});
export type PostAnalyticsEventsRequest = z.infer<typeof zPostAnalyticsEventsRequest>;

export const zPostAnalyticsEventsResponse = z.object({
  inserted: z.number().int().nonnegative(),
});
export type PostAnalyticsEventsResponse = z.infer<typeof zPostAnalyticsEventsResponse>;

export const zPostProjectRequest = z.object({
  name: z.string(),
  sheetLink: z.string().optional(),
});
export type PostProjectRequest = z.infer<typeof zPostProjectRequest>;

export const zImportLegacyLabSheetRequest = z.object({
  sheetUrl: z.string().url(),
  organisationId: z.number().int().positive(),
  labWorkbookId: z.string().uuid().optional(),
});
export type ImportLegacyLabSheetRequest = z.infer<typeof zImportLegacyLabSheetRequest>;

export const zImportLegacyLabSheetResponse = z.object({
  labWorkbookId: z.string().uuid(),
  sampleSheetId: z.string().uuid(),
  organisationId: z.number().int().positive(),
  linkedSampleCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()).optional(),
});
export type ImportLegacyLabSheetResponse = z.infer<typeof zImportLegacyLabSheetResponse>;

export const zAssayTmUpdate = z.object({
  assay: z.string(),
  gene: z.string(),
  changedAt: z.string(),
  hasStandardCurve: z.boolean(),
  minMeltingTemperature: z.number().nullable(),
  maxMeltingTemperature: z.number().nullable(),
  loqCq: z.number().nullable(),
});
export type AssayTmUpdate = z.infer<typeof zAssayTmUpdate>;

export const zAssayTmUpdatesResponse = z.object({
  updates: z.array(zAssayTmUpdate),
});
export type AssayTmUpdatesResponse = z.infer<typeof zAssayTmUpdatesResponse>;

const zProjectImageRequest = z.object({
  filename: z.string(),
});
export type ProjectImageRequest = z.infer<typeof zProjectImageRequest>;

export const zAbundanceStats = z.object({
  min: z.number().optional(),
  firstQuartile: z.number().optional(),
  median: z.number().optional(),
  mean: z.number().optional(),
  thirdQuartile: z.number().optional(),
  max: z.number().optional(),
  outliers: z.array(z.number()).optional(),
});
export type AbundanceStats = z.infer<typeof zAbundanceStats>;

const zWorldmapResponse = z.record(
  // By grouping, country and group (+ all)
  z.string(),
  z.record(z.string(), z.record(z.string(), zAbundanceStats)),
);
export type WorldmapResponse = z.infer<typeof zWorldmapResponse>;

export const zReferenceDatum = z.object({
  mean: z.number().nullable(),
  n: z.number().optional(), // for research reference data only
});
export type ReferenceDatum = z.infer<typeof zReferenceDatum>;

export const ALL_GENES = 'ALL_GENES' as const;
export const GLOBAL = 'GLOBAL' as const;
export type GLOBAL = typeof GLOBAL;

// Continent
export const zReferenceContinent = z.union([z.nativeEnum(Continent), z.literal(GLOBAL)]);
export type ReferenceContinent = z.infer<typeof zReferenceContinent>;
export const zReferenceByC = z.record(zReferenceContinent, zReferenceDatum);
export type ReferenceByC = z.infer<typeof zReferenceByC>;

// Sampling point type or subtype
export const zReferenceSamplingPointType = z.union([zSampleType, zSampleSubType]);
export type ReferenceSamplingPointType = z.infer<typeof zReferenceSamplingPointType>;
export const zReferenceByEC = z.record(zReferenceSamplingPointType, zReferenceByC);
export type ReferenceByEC = z.infer<typeof zReferenceByEC>;

// Assay or target
export const zReferenceTarget = z.union([
  z.string().startsWith('AY'),
  zL2Targets,
  z.literal(L1Targets.ARG),
  z.literal(ALL_GENES),
]);
export type ReferenceTarget = z.infer<typeof zReferenceTarget>;
export const zReferenceByAEC = z.record(zReferenceTarget, zReferenceByEC);
export type ReferenceByAEC = z.infer<typeof zReferenceByAEC>;

// By MetricMode, Target/assay, Etype/subtype, Continent
export const zReferenceByMAEC = z.record(zReferenceMetric, zReferenceByAEC);
export type ReferenceByMAEC = z.infer<typeof zReferenceByMAEC>;

const zHeatmapResponse = z.object({
  [HeatmapType.ALL]: z.any(),
  [HeatmapType.DETECTED]: z.any(),
  [HeatmapType.QUANTIFIED]: z.any(),
  html: z.string(),
  csv: z.string(),
});
export type HeatmapResponse = z.infer<typeof zHeatmapResponse>;

export enum PoolingType {
  // Pooled project in the database
  // NONE - no pooling, samples copied as is
  SAMPLING_POINT = 'SAMPLING_POINT', // Sampling points with the same name, type, subtype and lat lon are pooled together

  // On the fly sample pooling in the overview
  SITE_AND_ADMIN_LEVEL = 'SITE_AND_ADMIN_LEVEL',
  SITE = 'SITE',
}

export enum PoolingMode {
  SKIP_MISSING = 'SKIP_MISSING',
  THROW_MISSING = 'THROW_MISSING',
}

export const zPoolingType = z.nativeEnum(PoolingType);
export const zPoolingMode = z.nativeEnum(PoolingMode);
export const zPooling = z.union([
  z.object({
    mode: zPoolingMode,
    type: z.literal(PoolingType.SITE_AND_ADMIN_LEVEL),
    level: z.number(),
  }),
  z
    .object({
      mode: zPoolingMode,
      type: z.union([z.literal(PoolingType.SAMPLING_POINT), z.literal(PoolingType.SITE)]),
      level: z.number().optional(),
    })
    .omit({ level: true }),
]);
export type Pooling = z.infer<typeof zPooling>;

// NOTE: strive to keep this backwards compatible
// so that future code versions could (subject to data availability)
// re-calculate pooled projects after calculating new result versions
// for constituent projects
export const zPoolProjectRequest = z.object({
  name: z.string(),
  assaySetProjectId: z.number().optional(),
  projectIds: z.array(z.number()).optional(),
  minAssays: z.number().optional(),
  targets: z.array(z.union([zL2Targets, z.literal(carbapenem)])).optional(),
  environmentTypes: z.array(zSampleType).optional(),
  countries: z.array(z.string()).optional(),
  includeRegexStr: z.string().optional(),
  excludeRegexStr: z.string().optional(),
  analyzeStartDate: z.string().optional(),
  analyzeEndDate: z.string().optional(),
  pooling: zPooling.optional(),
});
export type PoolProjectRequest = z.infer<typeof zPoolProjectRequest>;

export interface PooledProjectCheckResponse {
  numAssays: number;
  numIncludedSamples: number;
  numIncludedSmplPnts: number;
  numSamplesToBeCreated: number;
  numSmplPntsToBeCreated: number;
  numProjects: number;
  numCities: number;
  numRegions: number;
  numCountries: number;
}

export const zSignupUserRequest = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  organisation: z.string().min(1, { message: 'Organisation is required' }),
  position: z.string().min(1, { message: 'Position is required' }),
  password: z
    .string()
    .min(1, { message: 'Password is required' })
    .refine(p => passwordRegExp.test(p), {
      message: passwordRequirementsMessage,
    }),
  signupCode: z.string().min(1, { message: 'Organisation code is required, please check your invitation link' }),
  verificationCode: z.string().optional(),
});
export type SignupUserRequest = z.infer<typeof zSignupUserRequest>;

export const zSignupInviteRequest = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  password: z
    .string()
    .min(1, { message: 'Password is required' })
    .refine(p => passwordRegExp.test(p), {
      message: passwordRequirementsMessage,
    }),
  inviteCode: z.string().min(1, { message: 'Invite code is required, please check your invitation link' }),
});
export type SignupInviteRequest = z.infer<typeof zSignupInviteRequest>;

export const zVerifyUserRequest = z.object({
  verificationCode: z.string(),
});
export type VerifyUserRequest = z.infer<typeof zVerifyUserRequest>;

export const zPasswordResetRequest = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});
export type PasswordResetRequest = z.infer<typeof zPasswordResetRequest>;

export const zPasswordResetFinalizeRequest = z.object({
  passwordResetCode: z.string(),
  password: z
    .string()
    .min(1, { message: 'Password is required' })
    .refine(p => passwordRegExp.test(p), {
      message: passwordRequirementsMessage,
    }),
});
export type PasswordResetFinalizeRequest = z.infer<typeof zPasswordResetFinalizeRequest>;

const zOrganisationAccessGrantObject = z.object({
  id: z.number(),
  accessLevel: zAccessLevel.default(AccessLevel.READ),
});
export const zOrganisationAccessGrant = z.union([z.number(), zOrganisationAccessGrantObject]);
export type OrganisationAccessGrant = z.infer<typeof zOrganisationAccessGrant>;

const zOrganisationRequest = z.object({
  id: z.number().optional(),
  name: z.string().min(1, { message: 'Organisation name is required' }),
  externalName: z.string().nullable().optional(),
  type: zOrganisationType.optional(),
  parentId: z.number().int().positive().nullable().optional(),
  projectAccesses: z.array(zOrganisationAccessGrant),
  samplingPointAccesses: z.array(zOrganisationAccessGrant),
  sampleAccesses: z.array(zOrganisationAccessGrant),
  features: z.array(zFeature),
  labFeatures: z.array(zLabFeature),
  isDemo: z.boolean(),
  defaultMetric: zMetricMode.optional(),
});
export const zPostOrganisationRequest = zOrganisationRequest.extend({
  type: zOrganisationType.default(OrganisationType.CUSTOMER),
  externalName: zOrganisationRequest.shape.externalName.default(null),
  parentId: z.number().int().positive(),
  projectAccesses: zOrganisationRequest.shape.projectAccesses.default([]),
  samplingPointAccesses: zOrganisationRequest.shape.samplingPointAccesses.default([]),
  sampleAccesses: zOrganisationRequest.shape.sampleAccesses.default([]),
  features: zOrganisationRequest.shape.features.default([]),
  labFeatures: zOrganisationRequest.shape.labFeatures.default([]),
  isDemo: zOrganisationRequest.shape.isDemo.default(false),
});
export type PostOrganisationRequest = z.infer<typeof zPostOrganisationRequest>;

const zPatchOrganisationRequest = zOrganisationRequest.partial().extend({
  id: z.number(),
});
export const zPatchOrPostOrganisationRequest = z.union([zPatchOrganisationRequest, zPostOrganisationRequest]);
export type PatchOrPostOrganisationRequest = z.infer<typeof zPatchOrPostOrganisationRequest>;

export const zOrganisationProjectAccessOptionsResponse = z.object({
  projectSampleCount: z.number(),
  projectSamplingPointCount: z.number(),
  currentVisibleProjectSampleCount: z.number(),
  projectSamplesWithoutSamplingPointCount: z.number(),
  samplingPointOption: z.object({
    samplingPointCount: z.number(),
    totalSamples: z.number(),
    outsideProjectSamples: z.number(),
  }),
});
export type OrganisationProjectAccessOptionsResponse = z.infer<typeof zOrganisationProjectAccessOptionsResponse>;

export const zXAxisModePayload = z.object({
  xAxisMode: z.nativeEnum(XAxisMode),
});
export type XAxisModePayload = z.infer<typeof zXAxisModePayload>;

export const zQaLabelBatchRequest = z
  .object({
    chipId: z.number(),
    chipName: z.string(),
    url: z.string().trim().min(1),
    scope: zQaLabelScope,
    entries: z.array(zQaLabelScopeEntry).min(1),
    labelType: zQaLabelType,
    tm: z.number().nullable(),
    note: z.string(),
    appliesToResults: z.boolean(),
  })
  .refine(
    ({ labelType, tm }) =>
      tm == null ||
      labelType === QaLabelType.PRODUCT ||
      labelType === QaLabelType.PRIMER_DIMER ||
      labelType === QaLabelType.UNSPECIFIC,
    { message: 'tm is only allowed for PRODUCT, PRIMER_DIMER, and UNSPECIFIC' },
  )
  .refine(({ tm }) => tm == null || isQaLabelTmInRange(tm), {
    message: `tm must be an integer between ${QA_LABEL_TM_MIN} and ${QA_LABEL_TM_MAX}`,
  })
  .superRefine(({ labelType, appliesToResults }, ctx) => {
    if (!appliesToResults || doesQaLabelTypeAllowResultApplication(labelType)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: getQaInvalidResultApplicationMessage(labelType),
      path: ['appliesToResults'],
    });
  })
  .superRefine(({ scope, labelType }, ctx) => {
    if (isQaLabelScopeAllowedForType(labelType, scope)) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: getQaInvalidScopeMessage(labelType, scope),
      path: ['scope'],
    });
  });
export type QaLabelBatchRequest = z.infer<typeof zQaLabelBatchRequest>;

export const zQaLabelBatchResponse = z.object({
  batchId: z.string(),
  labels: z.array(zQaLabel),
  affectedSampleIds: z.array(z.number()),
});
export type QaLabelBatchResponse = z.infer<typeof zQaLabelBatchResponse>;

export const zQaLabelDeleteRequest = z.object({
  chipId: z.number(),
  chipName: z.string(),
  labelIds: z.array(z.number().int().positive()).min(1),
});
export type QaLabelDeleteRequest = z.infer<typeof zQaLabelDeleteRequest>;

export const zQaLabelDeleteResponse = z.object({
  deletedLabelIds: z.array(z.number()),
  affectedSampleIds: z.array(z.number()),
});
export type QaLabelDeleteResponse = z.infer<typeof zQaLabelDeleteResponse>;

// GET /api/project/{id}/cts: the Cq rows of the project's visible samples by sample id and assay. The rows are the
// same as the abundance `cts` rows of GET /api/project/{id}?cts=true, which omits them by default because of their size.
export const zProjectCtsResponse = z.object({
  ctsBySampleId: z.record(z.string(), z.record(z.string(), z.array(zCtTableColumns))),
});
export type ProjectCtsResponse = z.infer<typeof zProjectCtsResponse>;

export const zQaLabelNoteRequest = z.object({
  chipId: z.number(),
  chipName: z.string(),
  labelIds: z.array(z.number().int().positive()).min(1),
  note: z.string(),
});
export type QaLabelNoteRequest = z.infer<typeof zQaLabelNoteRequest>;

export const zQaLabelNoteResponse = z.object({
  labels: z.array(zQaLabel),
});
export type QaLabelNoteResponse = z.infer<typeof zQaLabelNoteResponse>;

export const zQaLabelToggleResultStatusRequest = z.object({
  chipId: z.number(),
  chipName: z.string(),
  labelIds: z.array(z.number().int().positive()).min(1),
  appliesToResults: z.boolean(),
});
export type QaLabelToggleResultStatusRequest = z.infer<typeof zQaLabelToggleResultStatusRequest>;

export const zQaLabelToggleResultStatusResponse = z.object({
  labels: z.array(zQaLabel),
  affectedSampleIds: z.array(z.number()),
});
export type QaLabelToggleResultStatusResponse = z.infer<typeof zQaLabelToggleResultStatusResponse>;

export const zQaLabelUndoRequest = z.object({
  chipId: z.number(),
  chipName: z.string(),
  batchId: z.string(),
});
export type QaLabelUndoRequest = z.infer<typeof zQaLabelUndoRequest>;

export const zQaLabelUndoResponse = z.object({
  batchId: z.string(),
  affectedSampleIds: z.array(z.number()),
});
export type QaLabelUndoResponse = z.infer<typeof zQaLabelUndoResponse>;

// ----------------------------- Reproducibility request -----------------------------
// The consensus method supports one or more independent reproducibility "blocks". Each block has two or more
// "sets" (chips/runs/methods/columns) and a list of "sample groups" (conceptual sample rows) whose sample ids are
// aligned to the block's sets. Legacy two-set inputs (chipIdA/chipIdB, sampleIdPairs) are still accepted and are
// normalized into a single two-set block server-side.

export const zReproducibilitySampleGroupInput = z.object({
  label: z.string().optional(),
  // One sample id per set, in the same order as the block's setLabels. null means "no sample for this set".
  sampleIds: z.array(z.number().nullable()),
});
export type ReproducibilitySampleGroupInput = z.infer<typeof zReproducibilitySampleGroupInput>;

export const zReproducibilityAssayScope = z.enum(['all', 'shared']);
export type ReproducibilityAssayScope = z.infer<typeof zReproducibilityAssayScope>;

export const zReproducibilityBlockInput = z
  .object({
    label: z.string().optional(),
    setLabels: z.array(z.string()).min(2),
    // Optional chip id per set. When present, each set's CT rows are scoped to that chip instead of using the latest
    // sample result across all chips.
    chipIds: z.array(z.number()).optional(),
    // "all" preserves the legacy union-of-assays behavior. "shared" analyses only assays found in every present set
    // for each sample group.
    assayScope: zReproducibilityAssayScope.optional(),
    sampleGroups: z.array(zReproducibilitySampleGroupInput).min(1),
  })
  .superRefine((block, ctx) => {
    if (block.chipIds != null && block.chipIds.length !== block.setLabels.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Block has ${block.chipIds.length} chip ids but ${block.setLabels.length} sets`,
        path: ['chipIds'],
      });
    }

    // Each sample group must carry exactly one sample id per set, otherwise the analysis would silently pad/truncate.
    block.sampleGroups.forEach((group, idx) => {
      if (group.sampleIds.length !== block.setLabels.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Sample group ${idx + 1} has ${group.sampleIds.length} sample ids but the block has ${block.setLabels.length} sets`,
          path: ['sampleGroups', idx, 'sampleIds'],
        });
      }
    });
  });
export type ReproducibilityBlockInput = z.infer<typeof zReproducibilityBlockInput>;

export const zChipReproducibilityRequest = z
  .object({
    // Preferred new multi-block shape.
    blocks: z.array(zReproducibilityBlockInput).optional(),
    // Multi-chip auto-pairing: two or more chip ids, paired by sorted sample id into one block.
    chipIds: z.array(z.number()).optional(),
    // Legacy two-chip auto-pairing.
    chipIdA: z.number().optional(),
    chipIdB: z.number().optional(),
    // Legacy explicit two-set sample id pairs.
    sampleIdPairs: z.array(z.tuple([z.number(), z.number()])).optional(),
  })
  .refine(
    value =>
      (value.blocks?.length ?? 0) > 0 ||
      (value.chipIds?.length ?? 0) >= 2 ||
      (value.sampleIdPairs?.length ?? 0) > 0 ||
      (typeof value.chipIdA === 'number' &&
        Number.isFinite(value.chipIdA) &&
        typeof value.chipIdB === 'number' &&
        Number.isFinite(value.chipIdB)),
    {
      message: 'Provide blocks, chipIds (≥2), sampleIdPairs, or chipIdA/chipIdB',
    },
  );
export type ChipReproducibilityRequest = z.infer<typeof zChipReproducibilityRequest>;

export const zChipReproducibilitySample = z.object({
  sampleId: z.number(),
  samplingPointName: z.string().nullable(),
  time: z.string().nullable(),
});
export type ChipReproducibilitySample = z.infer<typeof zChipReproducibilitySample>;

export const zChipReproducibilitySamplePair = z.object({
  a: zChipReproducibilitySample,
  b: zChipReproducibilitySample,
});
export type ChipReproducibilitySamplePair = z.infer<typeof zChipReproducibilitySamplePair>;

// Bland–Altman plot types (kept as pairwise diagnostics, no longer the primary decision surface).
export const zBlandAltmanPoint = z.object({
  sampleIdA: z.number(),
  sampleIdB: z.number(),
  assay: z.string(),
  meanA: z.number(),
  meanB: z.number(),
  mean: z.number(), // (meanA + meanB)/2
  delta: z.number(), // meanA - meanB
});
export type BlandAltmanPoint = z.infer<typeof zBlandAltmanPoint>;

export const zBlandAltman = z.object({
  points: z.array(zBlandAltmanPoint),
  meanDelta: z.number(),
  sdDelta: z.number(),
  loaLower: z.number(),
  loaUpper: z.number(),
  rmsDelta: z.number(),
  meanAbsDelta: z.number(),
  withinTolerancePercent: z.number(),
});
export type BlandAltman = z.infer<typeof zBlandAltman>;

// ----------------------------- Reproducibility response -----------------------------

export const zReproVerdict = z.enum(['PASS', 'INCONCLUSIVE', 'FAIL']);
export type ReproVerdict = z.infer<typeof zReproVerdict>;

export const zReproConfidenceInterval = z.object({
  point: z.number(),
  lower: z.number(),
  upper: z.number(),
});
export type ReproConfidenceInterval = z.infer<typeof zReproConfidenceInterval>;

// Provisional decision thresholds, echoed back so the UI can render the exact rule that produced a verdict.
export const zReproThresholds = z.object({
  cqQuantificationThreshold: z.number(),
  madPassUpperCi: z.number(),
  madFailPoint: z.number(),
  madFailLowerCi: z.number(),
  qaPassUpperCi: z.number(),
  qaFailPoint: z.number(),
  qaFailLowerCi: z.number(),
  setQaFailPoint: z.number(),
  setQaFailLowerCi: z.number(),
  spreadPassUpperCi: z.number(),
  spreadFailLowerCi: z.number(),
  setMadFailMax: z.number(),
  minSets: z.number(),
  minSampleGroups: z.number(),
  minGlobalQuantObs: z.number(),
  minSetQuantObsForHardFail: z.number(),
});
export type ReproThresholds = z.infer<typeof zReproThresholds>;

export const zReproSetSummary = z.object({
  blockIndex: z.number(),
  setIndex: z.number(),
  label: z.string(),
  mad: zReproConfidenceInterval,
  signedShift: zReproConfidenceInterval,
  qaMismatchPercent: zReproConfidenceInterval,
  quantObsCount: z.number(),
  presentPointCount: z.number(),
  flaggedHeterogeneous: z.boolean(),
  heterogeneityReasons: z.array(z.string()),
});
export type ReproSetSummary = z.infer<typeof zReproSetSummary>;

export const zReproBlockSummary = z.object({
  blockIndex: z.number(),
  label: z.string(),
  setLabels: z.array(z.string()),
  nSets: z.number(),
  nSampleGroups: z.number(),
  setMadSpread: zReproConfidenceInterval,
  maxSetMad: z.number(),
  heterogeneityVerdict: zReproVerdict,
  sets: z.array(zReproSetSummary),
});
export type ReproBlockSummary = z.infer<typeof zReproBlockSummary>;

export const zReproSampleSummary = z.object({
  blockIndex: z.number(),
  sampleGroupIndex: z.number(),
  label: z.string(),
  consensusMad: z.number(),
  qaMismatchPercent: z.number(),
  matchedNonDetectionPercent: z.number(),
  quantObsCount: z.number(),
});
export type ReproSampleSummary = z.infer<typeof zReproSampleSummary>;

export const zReproAssaySummary = z.object({
  assay: z.string(),
  consensusMad: z.number(),
  qaMismatchPercent: z.number(),
  matchedNonDetectionPercent: z.number(),
  quantObsCount: z.number(),
});
export type ReproAssaySummary = z.infer<typeof zReproAssaySummary>;

export const zReproEvidence = z.object({
  nBlocks: z.number(),
  minSetsPerBlock: z.number(),
  minSampleGroupsPerBlock: z.number(),
  quantObsCount: z.number(),
  matchedNonDetectionObsCount: z.number(),
  qaPointCount: z.number(),
  sufficientForQuant: z.boolean(),
  sufficientStructure: z.boolean(),
});
export type ReproEvidence = z.infer<typeof zReproEvidence>;

export const zReproReport = z.object({
  conclusion: zReproVerdict,
  reasons: z.array(z.string()),
  thresholds: zReproThresholds,
  pooledConsensusMad: zReproConfidenceInterval,
  pooledQaMismatchPercent: zReproConfidenceInterval,
  matchedNonDetectionPercent: z.number(),
  quantVerdict: zReproVerdict,
  qaVerdict: zReproVerdict,
  heterogeneityVerdict: zReproVerdict,
  evidence: zReproEvidence,
  blocks: z.array(zReproBlockSummary),
  worstSamples: z.array(zReproSampleSummary),
  worstAssays: z.array(zReproAssaySummary),
});
export type ReproReport = z.infer<typeof zReproReport>;

export const zReproPairwiseComparison = z.object({
  blockIndex: z.number(),
  blockLabel: z.string(),
  setIndexA: z.number(),
  setIndexB: z.number(),
  labelA: z.string(),
  labelB: z.string(),
  n: z.number(), // number of sample-group pairs with data
  consensusMad: z.number(),
  qaMismatchPercent: z.number(),
  matchedNonDetectionPercent: z.number(),
  totalSampleAssayPairs: z.number(), // sample-assay points where both sets have a sample present
  qaMismatchPairs: z.number(), // points excluded by QA mismatch (exactly one side valid)
  matchedNonDetectionPairs: z.number(), // points excluded as matched (agreed) non-detections
  madPairs: z.number(), // points included in the consensus MAD
  statistic: z.number(), // normalized sign-flip test statistic: ||mean(d)||² / k (k=assay dimensions included)
  pValue: z.number(),
  blandAltman: zBlandAltman,
  samplePairs: z.array(zChipReproducibilitySamplePair),
});
export type ReproPairwiseComparison = z.infer<typeof zReproPairwiseComparison>;

export const zReproResolvedCell = z.object({
  sampleId: z.number().nullable(),
  samplingPointName: z.string().nullable(),
  time: z.string().nullable(),
});
export type ReproResolvedCell = z.infer<typeof zReproResolvedCell>;

export const zReproResolvedSampleGroup = z.object({
  label: z.string(),
  cells: z.array(zReproResolvedCell),
});
export type ReproResolvedSampleGroup = z.infer<typeof zReproResolvedSampleGroup>;

export const zReproResolvedBlock = z.object({
  blockIndex: z.number(),
  label: z.string(),
  setLabels: z.array(z.string()),
  sampleGroups: z.array(zReproResolvedSampleGroup),
});
export type ReproResolvedBlock = z.infer<typeof zReproResolvedBlock>;

export const zChipReproducibilityResponse = z.object({
  report: zReproReport,
  pairwise: z.array(zReproPairwiseComparison),
  blocks: z.array(zReproResolvedBlock),
});
export type ChipReproducibilityResponse = z.infer<typeof zChipReproducibilityResponse>;

// Admin APIs

export const zAdminImpersonationStartRequest = z.discriminatedUnion('targetType', [
  z.object({
    targetType: z.literal('EXISTING_USER'),
    organisationId: z.number().int().positive(),
    userId: z.number().int().positive(),
  }),
  z.object({
    targetType: z.literal('PSEUDO_USER'),
    organisationId: z.number().int().positive(),
    role: zOrganisationUserRole,
  }),
]);
export type AdminImpersonationStartRequest = z.infer<typeof zAdminImpersonationStartRequest>;

export const zAdminImpersonationStatusUser = z.object({
  id: z.number().int().positive(),
  email: z.string(),
  fullName: z.string(),
});
export type AdminImpersonationStatusUser = z.infer<typeof zAdminImpersonationStatusUser>;

export const zAdminImpersonationStatusResponse = z.object({
  active: z.boolean(),
  realUser: zAdminImpersonationStatusUser.nullable(),
  effectiveUser: zAdminImpersonationStatusUser.nullable(),
  targetOrganisation: z
    .object({
      id: z.number().int().positive(),
      name: z.string(),
      role: zOrganisationUserRole.nullable(),
      isPseudoUser: z.boolean(),
    })
    .nullable(),
});
export type AdminImpersonationStatusResponse = z.infer<typeof zAdminImpersonationStatusResponse>;

export const zAdminUserDirectoryUser = z.object({
  id: z.number(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  latestVerificationEmail: zEmailDeliverySummary.nullable(),
  latestPasswordResetEmail: zEmailDeliverySummary.nullable(),
});
export type AdminUserDirectoryUser = z.infer<typeof zAdminUserDirectoryUser>;

export const zAdminUserDirectoryOrganisationUser = z.object({
  organisationId: z.number(),
  userId: z.number(),
  role: zOrganisationUserRole,
});
export type AdminUserDirectoryOrganisationUser = z.infer<typeof zAdminUserDirectoryOrganisationUser>;

export const zAdminUserDirectoryResponse = z.object({
  users: z.array(zAdminUserDirectoryUser),
  organisationUsers: z.array(zAdminUserDirectoryOrganisationUser),
});
export type AdminUserDirectoryResponse = z.infer<typeof zAdminUserDirectoryResponse>;

// Samples list
export const zAdminSampleProject = z.object({ id: z.number(), name: z.string() });
export type AdminSampleProject = z.infer<typeof zAdminSampleProject>;

export const zAdminSampleRow = z.object({
  sampleId: z.number(),
  number: z.number().nullable(),
  bioRep: z.string().nullable(),
  status: z.string(),
  samplingPoint: z.object({ id: z.number(), name: z.string(), type: z.string(), country: z.string().nullable() }),
  projects: z.array(zAdminSampleProject),
  chipsCount: z.number(),
  labsheetCount: z.number(),
  updatedAt: z.string(),
});
export type AdminSampleRow = z.infer<typeof zAdminSampleRow>;

export const zAdminSamplesResponse = z.object({ rows: z.array(zAdminSampleRow), total: z.number() });
export type AdminSamplesResponse = z.infer<typeof zAdminSamplesResponse>;

export const zAdminSubmitSampleSheetRequest = z.object({
  rows: z.array(zSampleSpecRow),
});
export type AdminSubmitSampleSheetRequest = z.infer<typeof zAdminSubmitSampleSheetRequest>;

export const zAdminSampleChip = z.object({
  id: z.number(),
  name: z.string(),
  runDate: z.string().nullable(),
  labSheetUrl: z.string().nullable(),
});
export type AdminSampleChip = z.infer<typeof zAdminSampleChip>;

export const zAdminSampleChipsResponse = z.array(zAdminSampleChip);
export type AdminSampleChipsResponse = z.infer<typeof zAdminSampleChipsResponse>;

export const zAdminSampleAccessResponse = z.object({
  organisations: z.array(z.object({ id: z.number(), name: z.string(), accessLevel: zAccessLevel })),
  users: z.array(
    z.object({
      id: z.number(),
      email: z.string().nullable(),
      firstName: z.string(),
      lastName: z.string(),
      accessLevel: zAccessLevel,
    }),
  ),
});
export type AdminSampleAccessResponse = z.infer<typeof zAdminSampleAccessResponse>;

export const zAdminOrganisationAccessMetadataResponse = z.object({
  samplingPoints: z.array(
    z.object({
      id: z.number(),
      name: z.string().nullable(),
      projectIds: z.array(z.number()),
    }),
  ),
  samples: z.array(
    z.object({
      id: z.number(),
      number: z.number().nullable(),
      bioRep: z.string().nullable(),
      samplingPointId: z.number().nullable(),
      samplingPointName: z.string().nullable(),
      projectIds: z.array(z.number()),
    }),
  ),
});
export type AdminOrganisationAccessMetadataResponse = z.infer<typeof zAdminOrganisationAccessMetadataResponse>;

export const zAdminOrganisationSampleRow = z.object({
  sampleId: z.number(),
  sampleNumber: z.number().nullable(),
  bioReplica: z.string().nullable(),
  status: z.nativeEnum(SampleStatus).nullable(),
  sampleDescription: z.string().nullable(),
  sampleType: zSampleType.nullable(),
  detailedSampleType: zSampleSubType.nullable(),
  flowRate: z.number().nullable(),
  operatedFlowRate: z.number().nullable(),
  suspendedSolids: z.number().nullable(),
  bod: z.number().nullable(),
  filteredVolume: z.number().nullable(),
  primElutedDnaVolume: z.number().nullable(),
  primElutedDnaConcentration: z.number().nullable(),
  primElutedDnaQuality280: z.number().nullable(),
  primElutedDnaQuality230: z.number().nullable(),
  primDilutedDnaVolume: z.number().nullable(),
  primDilutedDnaConcentration: z.number().nullable(),
  primDilutedDnaConcentrationUnavailable: z.boolean().optional(),
  primDilutedDnaQuality280: z.number().nullable(),
  primDilutedDnaQuality230: z.number().nullable(),
  primDnaExtractionKit: z.string().nullable(),
  sampleCollectionDate: z.string().nullable(),
  city: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  country: z.string().nullable(),
  latitudeLongitude: zCoordinates.nullable(),
  samplingPointId: z.number().nullable(),
  samplingPointOwnerOrganisationIds: z.array(z.number()).optional(),
  panelId: z.number().nullable(),
  sampleSheetId: z.string().uuid().nullable(),
});
export type AdminOrganisationSampleRow = z.infer<typeof zAdminOrganisationSampleRow>;

const zAdminOrganisationSampleRowWithoutId = zAdminOrganisationSampleRow.omit({
  sampleId: true,
  samplingPointId: true,
});

export const zAdminOrganisationSampleUpsertRow = zAdminOrganisationSampleRowWithoutId.extend({
  clientRowId: z.string().min(1).max(128).optional(),
  sampleId: z.number().int().positive().optional(),
  samplingPointId: z.number().int().positive().nullable().optional(),
  sampleSheetId: z.string().uuid().nullable().optional(),
});
export type AdminOrganisationSampleUpsertRow = z.infer<typeof zAdminOrganisationSampleUpsertRow>;

export const zAdminOrganisationGenePanelSummary = z.object({
  id: z.number(),
  organisationId: z.number(),
  name: z.string(),
  state: z.nativeEnum(GenePanelState),
});
export type AdminOrganisationGenePanelSummary = z.infer<typeof zAdminOrganisationGenePanelSummary>;

export const zOrgSamplingPointChoice = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  sampleType: zSampleType,
  detailedSampleType: zSampleSubType.nullable(),
  country: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  city: z.string().nullable(),
  latitudeLongitude: zCoordinates.nullable(),
  ownerOrganisationIds: z.array(z.number().int().positive()).default([]),
});
export type OrgSamplingPointChoice = z.infer<typeof zOrgSamplingPointChoice>;

export const zAdminOrganisationSamplesResponse = z.object({
  organisation: z.object({ id: z.number(), name: z.string() }),
  rows: z.array(zAdminOrganisationSampleRow),
  genePanels: z.array(zAdminOrganisationGenePanelSummary),
});
export type AdminOrganisationSamplesResponse = z.infer<typeof zAdminOrganisationSamplesResponse>;

export const zOrgSamplingPointChoicesResponse = z.object({
  organisation: z.object({ id: z.number(), name: z.string() }),
  samplingPoints: z.array(zOrgSamplingPointChoice),
  genePanels: z.array(zAdminOrganisationGenePanelSummary),
});
export type OrgSamplingPointChoicesResponse = z.infer<typeof zOrgSamplingPointChoicesResponse>;

export const zLabWorkbookOrganisation = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  type: zOrganisationType,
  parentId: z.number().int().positive().nullable(),
});
export type LabWorkbookOrganisation = z.infer<typeof zLabWorkbookOrganisation>;

export const zShipmentQuotaSummary = z.object({
  shippingRequestQuota: z.number().int().nonnegative(),
  shippingRequestsUsed: z.number().int().nonnegative(),
  shippingRequestsPending: z.number().int().nonnegative(),
  shippingRequestsAvailable: z.number().int().nonnegative(),
});
export type ShipmentQuotaSummary = z.infer<typeof zShipmentQuotaSummary>;

const zAdminLabWorkbookSummary = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  projectLead: z.string().nullable(),
  channelUrl: z.string().nullable(),
  status: zLabWorkbookStatus,
  genePanelId: z.number().int().positive().nullable(),
  genePanelName: z.string().nullable(),
  genePanelState: z.nativeEnum(GenePanelState).nullable(),
  sampleSheetId: z.string().uuid(),
  sampleSheetName: z.string().nullable(),
  labSheetUrl: z.string().nullable(),
  xlShipmentsAuthorised: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const zAdminOrganisationLabWorkbookSummary = zAdminLabWorkbookSummary.extend(zShipmentQuotaSummary.shape);
export type AdminOrganisationLabWorkbookSummary = z.infer<typeof zAdminOrganisationLabWorkbookSummary>;

export const zAdminOrganisationLabWorkbooksResponse = z.object({
  workbooks: z.array(zAdminOrganisationLabWorkbookSummary),
});
export type AdminOrganisationLabWorkbooksResponse = z.infer<typeof zAdminOrganisationLabWorkbooksResponse>;

export const zLabWorkbookLinkedGenePanel = z.object({
  id: z.number().int().positive(),
  state: z.nativeEnum(GenePanelState).nullable(),
  name: z.string().nullable(),
  defaultKey: z.enum(defaultGenePanelKeys).nullable(),
});
export type LabWorkbookLinkedGenePanel = z.infer<typeof zLabWorkbookLinkedGenePanel>;

export const zAdminLabWorkbookRow = zAdminLabWorkbookSummary.extend({
  organisation: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    type: zOrganisationType,
  }),
  assignedLabOrganisation: zLabWorkbookOrganisation.nullable(),
});
export type AdminLabWorkbookRow = z.infer<typeof zAdminLabWorkbookRow>;

export const zAdminLabWorkbooksResponse = z.object({
  workbooks: z.array(zAdminLabWorkbookRow),
});
export type AdminLabWorkbooksResponse = z.infer<typeof zAdminLabWorkbooksResponse>;

export const zLabWorkbookAssignmentOptionsResponse = z.object({
  assignedLabOrganisation: zLabWorkbookOrganisation.nullable(),
  controllingOrganisation: zLabWorkbookOrganisation.nullable(),
  assignableLabOrganisations: z.array(zLabWorkbookOrganisation),
});
export type LabWorkbookAssignmentOptionsResponse = z.infer<typeof zLabWorkbookAssignmentOptionsResponse>;

export const zAdminLabWorkbooksQuery = z.object({
  status: z.union([zLabWorkbookStatus, z.array(zLabWorkbookStatus)]).optional(),
  includeCompleted: zBooleanish.optional(),
  includeLegacy: zBooleanish.optional(),
});
export type AdminLabWorkbooksQuery = z.infer<typeof zAdminLabWorkbooksQuery>;

const zAdminOrganisationSamplesPatchOptions = z.object({
  populateSamplingPointLocation: z.boolean().optional(),
  resubmission: z.boolean().optional(),
  allowSamplingPointEditsInResubmission: z.boolean().optional(),
  allowMissingFilteredVolume: z.boolean().optional(),
});

export const zAdminOrganisationSamplesPatchRequest = z.object({
  rows: z.array(zAdminOrganisationSampleUpsertRow),
  options: zAdminOrganisationSamplesPatchOptions.optional(),
});
export type AdminOrganisationSamplesPatchRequest = z.infer<typeof zAdminOrganisationSamplesPatchRequest>;

export const zAdminOrganisationSamplesPatchResponse = z.object({
  rows: z.array(zAdminOrganisationSampleRow),
  touchedSamplingPointIds: z.array(z.number().int().positive()).optional(),
});
export type AdminOrganisationSamplesPatchResponse = z.infer<typeof zAdminOrganisationSamplesPatchResponse>;

const sheetPatchFieldLiterals = [...sampleSpecColumnKeys, 'samplingPointId', 'status', 'isReadOnly'] as const;
export type SheetPatchField = (typeof sheetPatchFieldLiterals)[number];

const sheetPatchFieldSet: ReadonlySet<SheetPatchField> = new Set(sheetPatchFieldLiterals);

export function isSheetPatchField(value: unknown): value is SheetPatchField {
  return typeof value === 'string' && sheetPatchFieldSet.has(value as SheetPatchField);
}

const zSheetPatchField = z.enum(sheetPatchFieldLiterals as unknown as [SheetPatchField, ...SheetPatchField[]]);

export const zSheetPatchOp = z.object({
  rowId: z.string(),
  field: zSheetPatchField,
  value: z.any().nullable(),
  baseRowVersion: z.number().int().nonnegative(),
  previousValue: z.any().optional(),
  clientTxnId: z.string().uuid().optional(),
});
export type SheetPatchOp = z.infer<typeof zSheetPatchOp>;

// The submitter confirms an explicit numbering before submitting; sending it back lets the server refuse a
// submission whose numbering moved in the meantime. The count matters on its own because legacy bio-replica rows
// share a sample number, so first and last alone do not pin down how many samples would be created.
export const zConfirmedSampleNumbering = z.object({
  first: z.number().int().positive(),
  last: z.number().int().positive(),
  count: z.number().int().positive(),
});
export type ConfirmedSampleNumbering = z.infer<typeof zConfirmedSampleNumbering>;

export const zClientMsg = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('subscribe'),
    sheetId: z.string().uuid(),
    lastSheetVersion: z.number().int().nonnegative().optional(),
  }),
  z.object({
    type: z.literal('patch'),
    sheetId: z.string().uuid(),
    ops: z.array(zSheetPatchOp).min(1),
  }),
  z.object({
    type: z.literal('createRows'),
    sheetId: z.string().uuid(),
    count: z.number().int().positive().max(500),
  }),
  z.object({
    type: z.literal('deleteRows'),
    sheetId: z.string().uuid(),
    rowIds: z.array(z.string()).min(1),
  }),
  z.object({
    type: z.literal('submit'),
    sheetId: z.string().uuid(),
    rowIds: z.array(z.string().min(1)).min(1),
    metadataLimitationsConfirmationToken: z.string().length(64).optional(),
    confirmProceedWithPartialVolumeNormalisation: z.boolean().optional(),
    expectedSampleNumbering: zConfirmedSampleNumbering.optional(),
  }),
  z.object({
    type: z.literal('beginSampleEdits'),
    sheetId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('cancelSampleEdits'),
    sheetId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('resubmitSampleEdits'),
    sheetId: z.string().uuid(),
    rowIds: z.array(z.string().min(1)).min(1),
    metadataLimitationsConfirmationToken: z.string().length(64).optional(),
    confirmProceedWithPartialVolumeNormalisation: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('resync'),
    sheetId: z.string().uuid(),
    fromVersion: z.number().int().nonnegative(),
  }),
]);
export type ClientMsg = z.infer<typeof zClientMsg>;

export const zRowDelta = z.object({
  rowId: z.string(),
  rowVersion: z.number().int().nonnegative(),
  set: z.record(z.string(), z.any()).optional(),
  clear: z.array(z.string()).optional(),
  validation: z.record(z.string(), z.array(z.string())).optional(),
  isReadOnly: z.boolean().optional(),
  rowIndex: z.number().int().optional(),
  sampleId: z.number().nullable().optional(),
  samplingPointId: z.number().nullable().optional(),
  deleted: z.boolean().optional(),
});
export type RowDelta = z.infer<typeof zRowDelta>;

export const zServerRowSnapshot = z.object({
  rowId: z.string(),
  rowVersion: z.number().int().nonnegative(),
  isReadOnly: z.boolean(),
  data: z.any(),
  derived: z.any(),
  validation: z.any(),
  sampleId: z.number().nullable(),
  samplingPointId: z.number().nullable(),
  rowIndex: z.number(),
});
export type ServerRowSnapshot = z.infer<typeof zServerRowSnapshot>;

export const zServerMsg = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('init'),
    sheetId: z.string().uuid(),
    sheetVersion: z.number().int().nonnegative(),
    meta: z.record(z.string(), z.any()),
    rows: z.array(zServerRowSnapshot),
    genePanelId: z.number().int().positive().nullable(),
    labWorkbookId: z.string().uuid().nullable().optional(),
    labSheetUrl: z.string().nullable().optional(),
  }),
  z.object({
    type: z.literal('delta'),
    sheetId: z.string().uuid(),
    sheetVersion: z.number().int().nonnegative(),
    changes: z.array(zRowDelta),
    meta: z.record(z.string(), z.any()).optional(),
    genePanelId: z.number().int().positive().nullable().optional(),
  }),
  z.object({
    type: z.literal('ack'),
    sheetId: z.string().uuid(),
    ackTxnIds: z.array(z.string().uuid()),
  }),
  z.object({
    type: z.literal('error'),
    code: z.string(),
    message: z.string(),
    details: z.any().optional(),
  }),
  z.object({
    type: z.literal('submitted'),
    sheetId: z.string().uuid(),
    submitted: z.number().int().nonnegative(),
    locked: z.number().int().nonnegative(),
  }),
]);
export type ServerMsg = z.infer<typeof zServerMsg>;

export const zCreateSampleSheetRequest = z.object({
  genePanelId: z.number().int().positive().nullable().optional(),
  meta: z.record(z.string(), z.any()).optional(),
  name: z.string().max(200).nullable().optional(),
  projectLead: z.string().max(200).nullable().optional(),
  labSheetUrl: z.string().max(2048).nullable().optional(),
  channelUrl: z.string().max(2048).nullable().optional(),
  services: zLabWorkbookServices.nullable().optional(),
  shippingRequestQuota: z.number().int().min(0).max(1000).optional(),
  xlShipmentsAuthorised: z.boolean().optional(),
});
export type CreateSampleSheetRequest = z.infer<typeof zCreateSampleSheetRequest>;

export const zEnsureSampleSheetResponse = z.object({
  sheetId: z.string().uuid(),
  sheetVersion: z.number().int().nonnegative(),
  meta: z.record(z.string(), z.any()),
  genePanelId: z.number().int().positive().nullable(),
  labWorkbookId: z.string().uuid().nullable(),
  labSheetUrl: z.string().nullable(),
  labWorkbookName: z.string().nullable(),
  labWorkbookProjectLead: z.string().nullable(),
  labWorkbookChannelUrl: z.string().nullable(),
});
export type EnsureSampleSheetResponse = z.infer<typeof zEnsureSampleSheetResponse>;

export const zSampleSheetSnapshotResponse = z.object({
  sheetId: z.string().uuid(),
  sheetVersion: z.number().int().nonnegative(),
  meta: z.record(z.string(), z.any()),
  genePanelId: z.number().int().positive().nullable(),
  labWorkbookId: z.string().uuid().nullable(),
  labSheetUrl: z.string().nullable(),
  labWorkbookName: z.string().nullable(),
  labWorkbookProjectLead: z.string().nullable(),
  labWorkbookChannelUrl: z.string().nullable(),
  rows: z.array(zServerRowSnapshot),
});
export type SampleSheetSnapshotResponse = z.infer<typeof zSampleSheetSnapshotResponse>;

export const zSubmitSampleSheetResponse = z.object({
  sheetVersion: z.number().int().nonnegative(),
  changes: z.array(zRowDelta),
  submitted: z.number().int().nonnegative(),
  locked: z.number().int().nonnegative(),
});
export type SubmitSampleSheetResponse = z.infer<typeof zSubmitSampleSheetResponse>;

export const zPartialVolumeNormalisationConfirmation = z.object({
  status: z.literal('requires_partial_volume_confirmation'),
  applicableCount: z.number().int().nonnegative(),
  withVolumeCount: z.number().int().nonnegative(),
  withoutVolumeCount: z.number().int().nonnegative(),
  suppressedModes: z.tuple([
    z.literal(NormalisationMode.LITRE_16S),
    z.literal(NormalisationMode.LITRE_AY),
    z.literal(NormalisationMode.HOUR),
    z.literal(NormalisationMode.MG_SS),
    z.literal(NormalisationMode.MG_BOD),
  ]),
});
export type PartialVolumeNormalisationConfirmation = z.infer<typeof zPartialVolumeNormalisationConfirmation>;

export function buildPartialVolumeNormalisationConfirmationPayload(counts: {
  applicableCount: number;
  withVolumeCount: number;
  withoutVolumeCount: number;
}): PartialVolumeNormalisationConfirmation {
  return zPartialVolumeNormalisationConfirmation.parse({
    status: 'requires_partial_volume_confirmation',
    applicableCount: counts.applicableCount,
    withVolumeCount: counts.withVolumeCount,
    withoutVolumeCount: counts.withoutVolumeCount,
    suppressedModes: [...SUPPRESSED_VOLUME_MODES],
  });
}

export const zSampleMetadataLimitationField = z.enum([
  'filteredVolume',
  'primElutedDnaVolume',
  'primElutedDnaConcentration',
  'primElutedDnaQuality280',
  'primElutedDnaQuality230',
  'primDilutedDnaConcentration',
  'primDilutedDnaQuality280',
  'primDilutedDnaQuality230',
  'primDilutedDnaVolume',
]);
export type SampleMetadataLimitationField = z.infer<typeof zSampleMetadataLimitationField>;

export const zSampleMetadataLimitation = z.object({
  field: zSampleMetadataLimitationField,
  category: z.enum(['volume_normalisation', 'sample_volume', 'sample_quality']),
  reason: z.enum(['missing', 'not_available', 'below_recommended_minimum']),
  value: z.number().nullable(),
  recommendedMinimum: z.number().optional(),
  absoluteMinimum: z.number().optional(),
});
export type SampleMetadataLimitation = z.infer<typeof zSampleMetadataLimitation>;

export const zSampleMetadataLimitationRow = z.object({
  rowId: z.string().min(1),
  sampleNumber: z.number().int().positive().nullable(),
  bioReplica: z.string().nullable(),
  sampleDescription: z.string().nullable(),
  limitations: z.array(zSampleMetadataLimitation).min(1),
});
export type SampleMetadataLimitationRow = z.infer<typeof zSampleMetadataLimitationRow>;

export const zSampleMetadataLimitationsConfirmation = z.object({
  status: z.literal('requires_metadata_limitations_confirmation'),
  confirmationToken: z.string().length(64),
  affectedRowCount: z.number().int().positive(),
  rows: z.array(zSampleMetadataLimitationRow).min(1),
  partialVolumeNormalisation: zPartialVolumeNormalisationConfirmation.nullable(),
});
export type SampleMetadataLimitationsConfirmation = z.infer<typeof zSampleMetadataLimitationsConfirmation>;

export const zSubmitSampleSheetRequest = z.object({
  rowIds: z.array(z.string().min(1)).min(1),
  metadataLimitationsConfirmationToken: z.string().length(64).optional(),
  confirmProceedWithPartialVolumeNormalisation: z.boolean().optional(),
  expectedSampleNumbering: zConfirmedSampleNumbering.optional(),
});
export type SubmitSampleSheetRequest = z.infer<typeof zSubmitSampleSheetRequest>;

export const zUpdateSampleSheetGenePanelRequest = z.object({
  genePanelId: z.number().int().positive().nullable(),
});
export type UpdateSampleSheetGenePanelRequest = z.infer<typeof zUpdateSampleSheetGenePanelRequest>;

export const zUpdateSampleSheetGenePanelResponse = z.object({
  sheetVersion: z.number().int().nonnegative(),
  genePanelId: z.number().int().positive().nullable(),
  meta: z.record(z.string(), z.any()),
});
export type UpdateSampleSheetGenePanelResponse = z.infer<typeof zUpdateSampleSheetGenePanelResponse>;

export const zSamplingPointUsageRequest = z.object({
  samplingPointIds: z.array(z.number().int().positive()).min(1),
});
export type SamplingPointUsageRequest = z.infer<typeof zSamplingPointUsageRequest>;

export const zSamplingPointUsage = z.object({
  samplingPointId: z.number().int().positive(),
  linkedSamplesCount: z.number().int().nonnegative(),
  linkedDraftRowsCount: z.number().int().nonnegative(),
  isExclusive: z.boolean(),
});
export type SamplingPointUsage = z.infer<typeof zSamplingPointUsage>;

export const zSamplingPointUsageResponse = z.object({
  samplingPointUsage: z.array(zSamplingPointUsage),
});
export type SamplingPointUsageResponse = z.infer<typeof zSamplingPointUsageResponse>;

export const zAdminGenePanelAssayRow = z.object({
  assay: z.string(),
  gene: z.string(),
  targetAntibioticMajor: z.string(),
  hasStandardCurve: z.boolean(),
  genePackage1: z.boolean(),
  genePackage2: z.boolean(),
  genePackage21: z.boolean(),
  genePackage3: z.boolean(),
  forwardPrimer: z.string().nullable(),
  reversePrimer: z.string().nullable(),
  deprecated: z.boolean(),
  softLaunch: z.boolean(),
});
export type AdminGenePanelAssayRow = z.infer<typeof zAdminGenePanelAssayRow>;

export const zAdminGenePanelAssaysResponse = z.object({ assays: z.array(zAdminGenePanelAssayRow) });
export type AdminGenePanelAssaysResponse = z.infer<typeof zAdminGenePanelAssaysResponse>;

export const zAdminGenePanel = z.object({
  id: z.number(),
  organisationId: z.number(),
  name: z.string(),
  samples: z.number(),
  genes: z.number(),
  configurationId: zGenePanelConfiguration.optional(),
  assays: z.array(z.string()),
  state: z.nativeEnum(GenePanelState),
  selectionVersion: z.number().int().nonnegative(),
  createdBy: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminGenePanel = z.infer<typeof zAdminGenePanel>;

export const zAdminGenePanelsResponse = z.object({
  organisation: z.object({ id: z.number(), name: z.string() }),
  panels: z.array(zAdminGenePanel),
});
export type AdminGenePanelsResponse = z.infer<typeof zAdminGenePanelsResponse>;

export const zAdminGenePanelPatchRequest = z.object({
  id: z.number().optional(),
  name: z.string().min(1),
  configurationId: zGenePanelConfiguration,
  assays: z.array(z.string()),
});
export type AdminGenePanelPatchRequest = z.infer<typeof zAdminGenePanelPatchRequest>;

export const zAdminGenePanelPatchResponse = zAdminGenePanel;
export type AdminGenePanelPatchResponse = z.infer<typeof zAdminGenePanelPatchResponse>;

export const zAdminGenePanelSelectionPatchRequest = z.object({
  baseSelectionVersion: z.number().int().nonnegative(),
  addAssays: z.array(z.string()).optional().default([]),
  removeAssays: z.array(z.string()).optional().default([]),
});
export type AdminGenePanelSelectionPatchRequest = z.infer<typeof zAdminGenePanelSelectionPatchRequest>;

export const zAdminGenePanelSelectionPatchResponse = z.object({ panel: zAdminGenePanel });
export type AdminGenePanelSelectionPatchResponse = z.infer<typeof zAdminGenePanelSelectionPatchResponse>;

// SamplingPoints list
export const zAdminSamplingPointProject = z.object({ id: z.number(), name: z.string() });
export type AdminSamplingPointProject = z.infer<typeof zAdminSamplingPointProject>;

export const zAdminSamplingPointRow = z.object({
  samplingPointId: z.number(),
  name: z.string(),
  type: z.string(),
  country: z.string().nullable(),
  sampleCount: z.number(),
  projects: z.array(zAdminSamplingPointProject),
  updatedAt: z.string(),
});
export type AdminSamplingPointRow = z.infer<typeof zAdminSamplingPointRow>;

export const zAdminSamplingPointsResponse = z.object({ rows: z.array(zAdminSamplingPointRow), total: z.number() });
export type AdminSamplingPointsResponse = z.infer<typeof zAdminSamplingPointsResponse>;

export const zAdminSamplingPointSample = z.object({
  sampleId: z.number(),
  number: z.number().nullable(),
  bioRep: z.string().nullable(),
  chipsCount: z.number(),
  projectsCount: z.number(),
});
export type AdminSamplingPointSample = z.infer<typeof zAdminSamplingPointSample>;

export const zAdminSamplingPointSamplesResponse = z.array(zAdminSamplingPointSample);
export type AdminSamplingPointSamplesResponse = z.infer<typeof zAdminSamplingPointSamplesResponse>;

// Sampling point → projects with samples
export const zAdminSamplingPointProjectSample = z.object({
  id: z.number(),
  number: z.number().nullable(),
  bioRep: z.string().nullable(),
});
export type AdminSamplingPointProjectSample = z.infer<typeof zAdminSamplingPointProjectSample>;

export const zAdminSamplingPointProjectWithSamples = z.object({
  id: z.number(),
  name: z.string(),
  samples: z.array(zAdminSamplingPointProjectSample),
});
export type AdminSamplingPointProjectWithSamples = z.infer<typeof zAdminSamplingPointProjectWithSamples>;

export const zAdminSamplingPointProjectSamplesResponse = z.array(zAdminSamplingPointProjectWithSamples);
export type AdminSamplingPointProjectSamplesResponse = z.infer<typeof zAdminSamplingPointProjectSamplesResponse>;

export const zProcessFlow = z.object({
  id: z.number(),
  processId: z.number(),
  fromPointId: z.number(),
  toPointId: z.number(),
  kind: z.nativeEnum(ProcessFlowKind),
});
export type ProcessFlow = z.infer<typeof zProcessFlow>;

export const zProcess = z.object({
  id: z.number(),
  name: z.string(),
  kind: z.nativeEnum(ProcessKind),
  anchorSamplingPointId: z.number().nullable(),
  samplingPointIds: z.array(z.number()).optional(),
  flows: z.array(zProcessFlow).optional(),
});
export type Process = z.infer<typeof zProcess>;

export const zSamplingPointProcessInfo = z.object({
  process: zProcess,
  samplingPoints: z.array(zSamplingPoint),
});
export type SamplingPointProcessInfo = z.infer<typeof zSamplingPointProcessInfo>;

export const zPostSamplingPointProcessFlowRequest = z.object({
  fromPointId: z.number().int().positive(),
  toPointId: z.number().int().positive(),
});
export type PostSamplingPointProcessFlowRequest = z.infer<typeof zPostSamplingPointProcessFlowRequest>;

export const zPostSamplingPointProcessFlowResponse = z.object({
  processId: z.number().int().positive(),
  flowId: z.number().int().positive(),
  createdProcessIds: z.array(z.number().int().positive()),
  deletedProcessIds: z.array(z.number().int().positive()),
});
export type PostSamplingPointProcessFlowResponse = z.infer<typeof zPostSamplingPointProcessFlowResponse>;

export const zDeleteSamplingPointProcessFlowResponse = z.object({
  deletedFlowId: z.number().int().positive(),
  createdProcessIds: z.array(z.number().int().positive()),
  deletedProcessIds: z.array(z.number().int().positive()),
});
export type DeleteSamplingPointProcessFlowResponse = z.infer<typeof zDeleteSamplingPointProcessFlowResponse>;

export const zPostSamplingPointReverseProcessFlowResponse = z.object({
  flowId: z.number().int().positive(),
});
export type PostSamplingPointReverseProcessFlowResponse = z.infer<typeof zPostSamplingPointReverseProcessFlowResponse>;

// Sampling point GET payload (augmented for admin sampling point view)
export const zSamplingPointWithProjectSamples = zSamplingPoint.extend({
  projectSamples: z.array(zAdminSamplingPointProjectWithSamples),
  // Related: same coordinates/type + compatible subtype, and share at least one project
  relatedSamplingPoints: z.array(zSamplingPoint),
  // Duplicates: same subtype; if has lat/lon use those; otherwise require name match
  duplicateSamplingPoints: z.array(zSamplingPoint),
  processes: z.array(zSamplingPointProcessInfo).optional(),
  sampleSheets: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string().nullable(),
      sampleCount: z.number().int().nonnegative(),
      labWorkbookId: z.string().uuid().nullable(),
      labSheetUrl: z.string().nullable(),
    }),
  ),
});
export type SamplingPointWithProjectSamples = z.infer<typeof zSamplingPointWithProjectSamples>;

// Sample GET payload (admin view): sample core fields, sampling point link, and projects
export const zAdminSampleWithProjects = z.object({
  id: z.number(),
  number: z.number().nullable(),
  bioRep: z.string().nullable(),
  status: z.string().nullable(),
  time: z.string().nullable(),
  specificLat: z.number().nullable(),
  specificLon: z.number().nullable(),
  primDnaExtractionKit: z.string().nullable(),
  primDilutedDnaVolume: z.number().nullable(),
  primDilutedDnaConcentration: z.number().nullable(),
  primDilutedDnaQuality280: z.number().nullable(),
  primDilutedDnaQuality230: z.number().nullable(),
  primElutedDnaQuality280: z.number().nullable(),
  primElutedDnaQuality230: z.number().nullable(),
  primElutedDnaConcentration: z.number().nullable(),
  primElutedDnaVolume: z.number().nullable(),
  filteredVolume: z.number().nullable(),
  bod: z.number().nullable(),
  suspendedSolids: z.number().nullable(),
  operatedFlowRate: z.number().nullable(),
  flowRate: z.number().nullable(),
  samplingPoint: zSamplingPoint,
  projects: z.array(zAdminSampleProject),
  sampleSheet: z
    .object({
      id: z.string().uuid(),
      name: z.string().nullable(),
      labWorkbookId: z.string().uuid().nullable(),
      labSheetUrl: z.string().nullable(),
    })
    .nullable(),
});
export type AdminSampleWithProjects = z.infer<typeof zAdminSampleWithProjects>;

// Lab workbook primer prep
export const zLabPrimerPrepPlate = z.object({
  plate: z.number().int().positive(),
  grid: z.array(z.array(z.string().nullable())),
});
export type LabPrimerPrepPlate = z.infer<typeof zLabPrimerPrepPlate>;

export const zLabPrimerPrepWithBlanksRow = z.object({
  order: z.number().int().positive(),
  assay: z.string().nullable(),
  sourceWell: z.string().nullable(),
  sourcePlate: z.number().int().positive().nullable(),
});
export type LabPrimerPrepWithBlanksRow = z.infer<typeof zLabPrimerPrepWithBlanksRow>;

export const zLabPrimerPrepDoubleDippingRow = z.object({
  order: z.number().int().positive(),
  duplicateIndex: z.number().int().positive(),
  assay: z.string(),
  sourceWell: z.string().nullable(),
  sourcePlate: z.number().int().positive().nullable(),
});
export type LabPrimerPrepDoubleDippingRow = z.infer<typeof zLabPrimerPrepDoubleDippingRow>;

export const zLabPrimerPrepCompactedRow = z.object({
  order: z.number().int().positive(),
  assay: z.string(),
  sourceWell: z.string().nullable(),
  sourcePlate: z.number().int().positive().nullable(),
  targetPlate: z.number().int().positive(),
  targetWell: z.string(),
  plateRowColumn: z.string(),
});
export type LabPrimerPrepCompactedRow = z.infer<typeof zLabPrimerPrepCompactedRow>;

export const zLabPrimerPrepOpentronsRow = z.object({
  sourceLabware: z.string(),
  sourcePlate: z.union([z.number(), z.string()]).nullable(),
  sourceWell: z.string().nullable(),
  sourceAspirationHeightMm: z.number(),
  destinationLabware: z.string(),
  destinationSlot: z.string(),
  destinationWell: z.string(),
  volume: z.number().nullable(),
});
export type LabPrimerPrepOpentronsRow = z.infer<typeof zLabPrimerPrepOpentronsRow>;

export const zLabPrimerPrepOpentronsData = z.object({
  rows: z.array(zLabPrimerPrepOpentronsRow),
  csv: z.string(),
  suggestedFileName: z.string(),
  volumePerWell: z.number().nullable(),
});
export type LabPrimerPrepOpentronsData = z.infer<typeof zLabPrimerPrepOpentronsData>;

export const zLabPrimerPrepMetadata = z.object({
  selectedAssayCount: z.number().int().nonnegative(),
  placementCount: z.number().int().nonnegative(),
  missingAssays: z.array(z.string()),
  vendorAssayCount: z.number().int().nonnegative(),
  configurationSupportsDoubleDipping: z.boolean(),
  masterWaterVolume: z.number().nullable(),
});
export type LabPrimerPrepMetadata = z.infer<typeof zLabPrimerPrepMetadata>;

export const zLabPrimerPrepData = z.object({
  configurationId: zGenePanelConfiguration,
  dilutedPlate: z.object({
    rowLabels: z.array(z.string()),
    columnLabels: z.array(z.number()),
    plates: z.array(zLabPrimerPrepPlate),
    oneHealthGrid: z.array(z.array(z.string().nullable())),
  }),
  withBlanks: z.array(zLabPrimerPrepWithBlanksRow),
  doubleDipping: z.array(zLabPrimerPrepDoubleDippingRow).optional(),
  compacted: z.array(zLabPrimerPrepCompactedRow),
  opentrons: zLabPrimerPrepOpentronsData,
  opentronsFlex: zLabPrimerPrepOpentronsData,
  metadata: zLabPrimerPrepMetadata,
});
export type LabPrimerPrepData = z.infer<typeof zLabPrimerPrepData>;

export const zLabLegacyImportState = z.object({
  imported: z.boolean(),
  eligible: z.boolean(),
  reasons: z.array(z.string()),
});
export type LabLegacyImportState = z.infer<typeof zLabLegacyImportState>;
export const zLabInitials = z
  .string()
  .length(3, { message: 'Lab initials must be exactly three characters.' })
  .regex(/^[A-Z]{3}$/, { message: 'Lab initials must contain only letters A-Z.' })
  .transform(value => value.toUpperCase());
export type LabInitials = z.infer<typeof zLabInitials>;

const zIsoDateTimeString = z.preprocess(
  value => (value instanceof Date ? value.toISOString() : value),
  z.string().datetime({ offset: true }),
);

const zSmartChipRunLogUpdate = z.object({
  id: z.string().uuid().nullable(),
  chipId: z.number().int().positive().nullable(),
  chipIndex: z.number().int().nonnegative().nullable().optional(),
  chipName: z.string().nullable().optional(),
  runDateIso: zIsoDateTimeString.nullable(),
  comment: z.string().nullable(),
  invoice: z.string().nullable(),
  dispenser: z.number().int().nonnegative().nullable(),
  dispenserPrefix: zDispenserPrefix.nullable().optional(),
  cycler: z.number().int().nonnegative().nullable(),
  kitLot: z.string().nullable().optional(),
  mmLot: z.string().nullable().optional(),
  waterLot: z.string().nullable().optional(),
  glycerolLot: z.string().nullable().optional(),
  antifoamBLot: z.string().nullable().optional(),
  preparerInitials: zLabInitials,
  uploaderInitials: zLabInitials.nullable().optional(),
});
export type SmartChipRunLogUpdate = z.infer<typeof zSmartChipRunLogUpdate>;

export const zSmartChipRunUpdate = z.object({
  chipsToRun: z.number().int().min(1),
  selectedLayoutIndex: z.number().int().nonnegative(),
  runLogs: z.array(zSmartChipRunLogUpdate),
});
export type SmartChipRunUpdate = z.infer<typeof zSmartChipRunUpdate>;

export const zSmartChipRunUploadMetadata = z.object({
  chipId: z.number().int().positive().nullable().optional(),
  chipName: z
    .string()
    .trim()
    .min(1, { message: 'Chip name is required.' })
    .max(255, { message: 'Chip name must be at most 255 characters.' }),
  runDateIso: zIsoDateTimeString.nullable().optional(),
  dispenser: z.number().int().nonnegative().nullable().optional(),
  dispenserPrefix: zDispenserPrefix.nullable().optional(),
  cycler: z.number().int().nonnegative().nullable().optional(),
  kitLot: z.string().nullable().optional(),
  mmLot: z.string().nullable().optional(),
  waterLot: z.string().nullable().optional(),
  glycerolLot: z.string().nullable().optional(),
  antifoamBLot: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
  invoice: z.string().nullable().optional(),
  preparerInitials: zLabInitials,
  uploaderInitials: zLabInitials,
  overwriteExisting: z.boolean().optional(),
});
export type SmartChipRunUploadMetadata = z.infer<typeof zSmartChipRunUploadMetadata>;

export const zSmartChipRunUploadResponse = z.object({
  chipId: z.number().int().positive(),
  chipName: z.string(),
  projectId: z.number().int().positive(),
  projectName: z.string(),
  sampleCount: z.number().int().nonnegative(),
  replacedExisting: z.boolean(),
});
export type SmartChipRunUploadResponse = z.infer<typeof zSmartChipRunUploadResponse>;

export const zJobAcceptedResponse = z.object({ jobId: z.string().uuid() });
export type JobAcceptedResponse = z.infer<typeof zJobAcceptedResponse>;
export const zBackgroundJobStatus = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);
export const zBackgroundJobSummary = z.object({
  id: z.string().uuid(),
  canManage: z.boolean(),
  canRetry: z.boolean(),
  kind: z.string(),
  label: z.string(),
  status: zBackgroundJobStatus,
  attempt: z.number().int(),
  createdAt: z.date(),
  availableAt: z.date(),
  finishedAt: z.date().nullable(),
  message: z.string().nullable(),
  error: z.string().nullable(),
  result: zSmartChipRunUploadResponse.nullable(),
});
export type BackgroundJobSummary = z.infer<typeof zBackgroundJobSummary>;
export const zWorkbookJobsResponse = z.object({ jobs: z.array(zBackgroundJobSummary) });

export const zSmartChipGrid = z.object({
  rowLabels: z.array(z.string()),
  columnLabels: z.array(z.union([z.string(), z.number()])),
  values: z.array(z.array(z.string().nullable())),
  caption: z.string().nullable().optional(),
});
export type SmartChipGrid = z.infer<typeof zSmartChipGrid>;

export const zSmartChipSampleLayout = z.object({
  chipIndex: z.number().int().nonnegative(),
  chipLabel: z.string().nullable(),
  primary: zSmartChipGrid,
  secondary: zSmartChipGrid.nullable().optional(),
  sampleCount: z.number().int().nonnegative(),
  samplesPerChip: z.number().int().positive().nullable(),
  isComplete: z.boolean(),
});
export type SmartChipSampleLayout = z.infer<typeof zSmartChipSampleLayout>;

export const zSmartChipRunLogEntry = z.object({
  id: z.string().uuid().nullable(),
  chipId: z.number().int().positive().nullable(),
  chipIndex: z.number().int().nonnegative().nullable(),
  chipName: z.string().nullable(),
  runDateIso: zIsoDateTimeString.nullable(),
  comment: z.string().nullable(),
  invoice: z.string().nullable(),
  dispenser: z.number().int().nonnegative().nullable(),
  dispenserPrefix: zDispenserPrefix.nullable(),
  cycler: z.number().int().nonnegative().nullable(),
  kitLot: z.string().nullable(),
  mmLot: z.string().nullable(),
  waterLot: z.string().nullable(),
  glycerolLot: z.string().nullable(),
  antifoamBLot: z.string().nullable(),
  preparerInitials: zLabInitials.nullable().optional(),
  uploaderInitials: zLabInitials.nullable().optional(),
});
export type SmartChipRunLogEntry = z.infer<typeof zSmartChipRunLogEntry>;

export const zSmartChipRunFileDescriptor = z.object({
  filename: z.string(),
  url: z.string(),
});
export type SmartChipRunFileDescriptor = z.infer<typeof zSmartChipRunFileDescriptor>;

export const zSmartChipRunSummary = z.object({
  rawOnlyAnalysisEnabled: z.boolean().optional(),
  chips: z.array(
    z.object({
      id: z.number().int().positive(),
      name: z.string(),
      runDateIso: zIsoDateTimeString.nullable(),
      mmLot: z.string().nullable(),
      waterLot: z.string().nullable(),
      glycerolLot: z.string().nullable(),
      antifoamBLot: z.string().nullable(),
      kitLot: z.string().nullable(),
      comment: z.string().nullable(),
      invoice: z.string().nullable(),
      preparerInitials: zLabInitials.nullable(),
      uploaderInitials: zLabInitials.nullable(),
      dispenser: z.number().int().nonnegative().nullable(),
      dispenserPrefix: zDispenserPrefix.nullable(),
      cycler: z.number().int().nonnegative().nullable(),
      files: z.object({
        cts: zSmartChipRunFileDescriptor.nullable(),
        raw: zSmartChipRunFileDescriptor.nullable(),
      }),
    }),
  ),
  assayGrid: zSmartChipGrid.nullable(),
  sampleLayouts: z.array(zSmartChipSampleLayout),
  suggestedChipCount: z.number().int().positive().nullable(),
  runLogs: z.array(zSmartChipRunLogEntry),
  chipsToRun: z.number().int().min(1).nullable(),
  selectedLayoutIndex: z.number().int().nonnegative().nullable(),
  configurationId: zGenePanelConfiguration.nullable().optional(),
});
export type SmartChipRunSummary = z.infer<typeof zSmartChipRunSummary>;

export const zLabWorkbookResetEligibility = z.object({
  eligible: z.boolean(),
  reasons: z.array(z.string()),
});
export type LabWorkbookResetEligibility = z.infer<typeof zLabWorkbookResetEligibility>;

export const zLabWorkbookDeleteEligibility = z.object({
  eligible: z.boolean(),
  reasons: z.array(z.string()),
});
export type LabWorkbookDeleteEligibility = z.infer<typeof zLabWorkbookDeleteEligibility>;

export const zLabWorkbookSummary = z.object({
  workbook: zLabWorkbook,
  sampleSheet: z.object({
    id: z.string().uuid(),
    organisationId: z.number().int().positive(),
    organisation: zLabWorkbookOrganisation,
    name: z.string().nullable(),
    labSheetUrl: z.string().nullable(),
  }),
  assignedLabOrganisation: zLabWorkbookOrganisation.nullable(),
  controllingOrganisation: zLabWorkbookOrganisation.nullable(),
  linkedGenePanel: zLabWorkbookLinkedGenePanel.nullable(),
  sampleProgress: z.object({
    submitted: z.number().int().nonnegative(),
    minimumRequired: z.number().int().positive().nullable(),
  }),
  dnaProgress: z.object({
    hasMeasurements: z.boolean(),
  }),
  primerPrep: zLabPrimerPrepData.nullable(),
  legacyImport: zLabLegacyImportState,
  smartChip: zSmartChipRunSummary.nullable().optional(),
  resetEligibility: zLabWorkbookResetEligibility,
  deleteEligibility: zLabWorkbookDeleteEligibility,
  linkedProjects: z
    .array(
      z.object({
        id: z.number().int().positive(),
        name: z.string(),
      }),
    )
    .default([]),
});
export type LabWorkbookSummary = z.infer<typeof zLabWorkbookSummary>;
export { zLabDnaStepSummaryResponse, zMarkLabDnaStepReadyRequest, zSaveLabDnaStepRequest };
export type { LabDnaStepSummaryResponse, MarkLabDnaStepReadyRequest, SaveLabDnaStepRequest };

export const zUpdateLabWorkbookRequest = z
  .object({
    prescreening: z.boolean().optional(),
    oneHealthOverride: z.boolean().optional(),
    numChips: z.number().int().min(1).optional(),
    defaultMmLot: z.string().nullable().optional(),
    primerStorageFindLocation: z.string().nullable().optional(),
    primerStorageLeaveLocation: z.string().nullable().optional(),
    sampleStorageFindLocation: z.string().nullable().optional(),
    sampleStorageLeaveLocation: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    status: zLabWorkbookStatus.optional(),
    statusMessage: z.string().max(500).nullable().optional(),
    smartChipRun: zSmartChipRunUpdate.optional(),
  })
  .superRefine((value, ctx) => {
    if (value.prescreening && value.oneHealthOverride) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Prescreening and OneHealth override cannot both be enabled.',
      });
    }
    if (value.statusMessage !== undefined && value.status === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Status message can only be provided when updating the status.',
      });
    }
  });
export type UpdateLabWorkbookRequest = z.infer<typeof zUpdateLabWorkbookRequest>;

export const zUpdateLabWorkbookMetadataRequest = z.object({
  name: z.string().max(200).nullable().optional(),
  projectLead: z.string().max(200).nullable().optional(),
  channelUrl: z.string().max(2048).nullable().optional(),
  firstSampleNumber: z.number().int().positive().optional(),
});
export type UpdateLabWorkbookMetadataRequest = z.infer<typeof zUpdateLabWorkbookMetadataRequest>;

export const zLabWorkbookSummaryResponse = zLabWorkbookSummary;
export type LabWorkbookSummaryResponse = z.infer<typeof zLabWorkbookSummaryResponse>;

export const zMarkLabWorkbookReadyRequest = z.object({
  notes: z.string().nullable().optional(),
  /**
   * @deprecated No longer used by the UI. This was only stored in a status-transition log entry.
   */
  message: z.string().max(500).nullable().optional(),
});
export type MarkLabWorkbookReadyRequest = z.infer<typeof zMarkLabWorkbookReadyRequest>;

export const zAssignLabWorkbookRequest = z.object({
  labOrganisationId: z.number().int().positive().nullable(),
});
export type AssignLabWorkbookRequest = z.infer<typeof zAssignLabWorkbookRequest>;

export const zDeleteLabWorkbookResponse = z.object({
  deleted: z.literal(true),
});
export type DeleteLabWorkbookResponse = z.infer<typeof zDeleteLabWorkbookResponse>;

export const zOrganisationLabWorkbookScope = z.enum(['owned', 'subtree', 'assigned']);
export const zOrganisationLabWorkbooksQuery = z
  .object({
    scope: zOrganisationLabWorkbookScope.optional(),
    status: z.union([zLabWorkbookStatus, z.array(zLabWorkbookStatus)]).optional(),
  })
  .transform(value => ({
    scope: value.scope ?? 'owned',
    statuses: value.status === undefined ? [] : Array.isArray(value.status) ? value.status : [value.status],
  }));
export type OrganisationLabWorkbooksQuery = z.infer<typeof zOrganisationLabWorkbooksQuery>;

export const zOrganisationLabWorkbookOrganisation = zLabWorkbookOrganisation;

export const zOrganisationLabWorkbookEntry = z.object({
  workbook: zLabWorkbook,
  sampleSheet: z.object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    organisation: zOrganisationLabWorkbookOrganisation,
  }),
  assignedLabOrganisation: zOrganisationLabWorkbookOrganisation.nullable(),
});
export type OrganisationLabWorkbookEntry = z.infer<typeof zOrganisationLabWorkbookEntry>;

export const zOrganisationLabWorkbooksResponse = z.object({
  workbooks: z.array(zOrganisationLabWorkbookEntry),
});
export type OrganisationLabWorkbooksResponse = z.infer<typeof zOrganisationLabWorkbooksResponse>;

// Chips list (admin)
export const zAdminChipProject = z.object({ id: z.number(), name: z.string() });
export type AdminChipProject = z.infer<typeof zAdminChipProject>;

export const zAdminChipRow = z.object({
  chipId: z.number(),
  name: z.string(),
  runDate: z.string().nullable(),
  dispenser: z.number().nullable(),
  dispenserPrefix: zDispenserPrefix.optional(),
  cycler: z.number().nullable(),
  mmLot: z.string().nullable(),
  waterLot: z.string().nullable(),
  glycerolLot: z.string().nullable(),
  antifoamBLot: z.string().nullable(),
  kitLot: z.string().nullable(),
  projects: z.array(zAdminChipProject),
  updatedAt: z.string(),
});
export type AdminChipRow = z.infer<typeof zAdminChipRow>;

export const zAdminChipsResponse = z.object({ rows: z.array(zAdminChipRow), total: z.number() });
export type AdminChipsResponse = z.infer<typeof zAdminChipsResponse>;

export const zSensorName = z.enum(['dispensers', 'cyclers']);
export const zSensorCsvTimeZone = z.enum(['Europe/Helsinki', 'UTC']);
export type SensorCsvTimeZone = z.infer<typeof zSensorCsvTimeZone>;
export type SensorName = z.infer<typeof zSensorName>;
export const SENSOR_LABEL_BY_NAME: Record<SensorName, string> = {
  dispensers: 'Dispensing room',
  cyclers: 'SmartChip post-PCR',
};

export const zAdminSensorSummary = z.object({
  name: zSensorName,
  label: z.string(),
  rowCount: z.number().int().nonnegative(),
  latestMeasuredAt: z.string().nullable(),
  sync: z.object({
    status: z.enum(['NOT_CONFIGURED', 'IDLE', 'PENDING', 'SYNCING', 'ERROR']),
    lastSuccessAt: z.string().nullable(),
    error: z.string().nullable(),
  }),
});
export type AdminSensorSummary = z.infer<typeof zAdminSensorSummary>;

export const zAdminSensorsResponse = z.object({
  sensors: z.array(zAdminSensorSummary),
});
export type AdminSensorsResponse = z.infer<typeof zAdminSensorsResponse>;

export const zAdminSensorUploadResponse = z.object({
  sensorName: zSensorName,
  parsedRows: z.number().int().nonnegative(),
  insertedRows: z.number().int().nonnegative(),
  skippedBefore2026: z.number().int().nonnegative(),
  skippedExisting: z.number().int().nonnegative(),
  skippedDuplicateInFile: z.number().int().nonnegative(),
  latestMeasuredAt: z.string().nullable(),
});
export type AdminSensorUploadResponse = z.infer<typeof zAdminSensorUploadResponse>;

const zTimeOnlyString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
  message: 'Expected time in HH:mm format',
});
const zDateOnlyString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  message: 'Expected date in YYYY-MM-DD format',
});
const zCountryAlpha3String = z.string().trim().toUpperCase().length(3);

function toMinutes(timeValue: string): number {
  const [hour, minute] = timeValue.split(':').map(Number);
  return hour * 60 + minute;
}

function hasMinimumPickupWindowHours(start: string, end: string): boolean {
  return toMinutes(end) - toMinutes(start) >= 120;
}

export const zShipmentItemInput = z.object({
  description: z.string().trim().min(1).max(500),
  count: z.number().int().positive().max(100_000),
});
export type ShipmentItemInput = z.infer<typeof zShipmentItemInput>;

const zShipmentConfirmationUrl = z
  .string()
  .trim()
  .url()
  .refine(value => value.startsWith('https://'), 'Pickup confirmation link must use HTTPS.');

const zShipmentCreateOrEditFieldsBase = z.object({
  pickupDate: zDateOnlyString,
  pickupWindowStart: zTimeOnlyString,
  pickupWindowEnd: zTimeOnlyString,
  contactName: z.string().trim().min(1).max(200),
  contactEmail: z.string().email(),
  contactTelephoneNumber: z.string().trim().min(1).max(100).nullable().optional(),
  pickupCountry: zCountryAlpha3String.nullable().optional(),
  pickupAddressLine1: z.string().trim().min(1).max(300).nullable().optional(),
  pickupAddressLine2: z.string().trim().max(300).nullable().optional(),
  pickupAddressLine3: z.string().trim().max(300).nullable().optional(),
  pickupPostalCode: z.string().trim().max(80).nullable().optional(),
  pickupCity: z.string().trim().max(150).nullable().optional(),
  taxId: z.string().trim().max(120).nullable().optional(),
  packageWeightKg: z.number().positive().max(100_000).nullable().optional(),
  packageLengthCm: z.number().positive().max(100_000).nullable().optional(),
  packageWidthCm: z.number().positive().max(100_000).nullable().optional(),
  packageHeightCm: z.number().positive().max(100_000).nullable().optional(),
  items: z.array(zShipmentItemInput).min(1).max(50),
});

export const zCreateShipmentRequest = zShipmentCreateOrEditFieldsBase
  .extend({
    organisationId: z.number().int().positive(),
    labWorkbookId: z.string().uuid().nullable().optional(),
  })
  .refine(data => hasMinimumPickupWindowHours(data.pickupWindowStart, data.pickupWindowEnd), {
    message: 'Pickup window must be at least 2 hours.',
    path: ['pickupWindowEnd'],
  })
  .superRefine((data, ctx) => {
    const requiredTextFields: Array<{ path: keyof typeof data; value: string | null | undefined }> = [
      { path: 'contactTelephoneNumber', value: data.contactTelephoneNumber },
      { path: 'pickupCountry', value: data.pickupCountry },
      { path: 'pickupAddressLine1', value: data.pickupAddressLine1 },
      { path: 'pickupPostalCode', value: data.pickupPostalCode },
      { path: 'pickupCity', value: data.pickupCity },
      ...(isEuropeanUnionCountry(data.pickupCountry) ? [] : [{ path: 'taxId' as const, value: data.taxId }]),
    ];

    requiredTextFields.forEach(field => {
      if (!field.value || field.value.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: [field.path],
        });
      }
    });

    const requiredNumberFields: Array<{ path: keyof typeof data; value: number | null | undefined }> = [
      { path: 'packageWeightKg', value: data.packageWeightKg },
      { path: 'packageLengthCm', value: data.packageLengthCm },
      { path: 'packageWidthCm', value: data.packageWidthCm },
      { path: 'packageHeightCm', value: data.packageHeightCm },
    ];

    requiredNumberFields.forEach(field => {
      if (field.value == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: [field.path],
        });
      }
    });
  });
export type CreateShipmentRequest = z.infer<typeof zCreateShipmentRequest>;

export const zAdminPatchShipmentRequest = z
  .object({
    status: zShipmentStatus.optional(),
    pickupDate: zDateOnlyString.optional(),
    pickupWindowStart: zTimeOnlyString.optional(),
    pickupWindowEnd: zTimeOnlyString.optional(),
    contactName: z.string().trim().min(1).max(200).optional(),
    contactEmail: z.string().email().optional(),
    contactTelephoneNumber: z.string().trim().min(1).max(100).nullable().optional(),
    pickupCountry: zCountryAlpha3String.nullable().optional(),
    pickupAddressLine1: z.string().trim().min(1).max(300).nullable().optional(),
    pickupAddressLine2: z.string().trim().max(300).nullable().optional(),
    pickupAddressLine3: z.string().trim().max(300).nullable().optional(),
    pickupPostalCode: z.string().trim().max(80).nullable().optional(),
    pickupCity: z.string().trim().max(150).nullable().optional(),
    taxId: z.string().trim().max(120).nullable().optional(),
    packageWeightKg: z.number().positive().max(100_000).nullable().optional(),
    packageLengthCm: z.number().positive().max(100_000).nullable().optional(),
    packageWidthCm: z.number().positive().max(100_000).nullable().optional(),
    packageHeightCm: z.number().positive().max(100_000).nullable().optional(),
    confirmationUrl: zShipmentConfirmationUrl.nullable().optional(),
    items: z.array(zShipmentItemInput).min(1).max(50).optional(),
  })
  .refine(payload => Object.keys(payload).length > 0, 'At least one patch field is required when updating a shipment.')
  .refine(
    payload =>
      !payload.pickupWindowStart ||
      !payload.pickupWindowEnd ||
      hasMinimumPickupWindowHours(payload.pickupWindowStart, payload.pickupWindowEnd),
    {
      message: 'Pickup window must be at least 2 hours.',
      path: ['pickupWindowEnd'],
    },
  );
export type AdminPatchShipmentRequest = z.infer<typeof zAdminPatchShipmentRequest>;

export const zShipmentResponseItem = z.object({
  id: z.number().int().positive(),
  description: z.string(),
  count: z.number().int().positive(),
});
export type ShipmentResponseItem = z.infer<typeof zShipmentResponseItem>;

export const zShipmentResponseOrganisation = z.object({
  id: z.number().int().positive(),
  name: z.string(),
});
export type ShipmentResponseOrganisation = z.infer<typeof zShipmentResponseOrganisation>;

export const zShipmentResponseLabWorkbook = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
});
export type ShipmentResponseLabWorkbook = z.infer<typeof zShipmentResponseLabWorkbook>;

export const zShipmentResponseCreator = z.object({
  id: z.number().int().positive(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  telephoneNumber: z.string().nullable(),
});
export type ShipmentResponseCreator = z.infer<typeof zShipmentResponseCreator>;

export const zShipmentDocumentTitle = z.string().trim().min(1).max(120);

export const zShipmentDocumentResponse = z.object({
  id: z.number().int().positive(),
  title: zShipmentDocumentTitle,
  filename: z.string(),
  sizeBytes: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ShipmentDocumentResponse = z.infer<typeof zShipmentDocumentResponse>;

export const zShipmentResponse = z.object({
  id: z.number().int().positive(),
  organisationId: z.number().int().positive(),
  labWorkbookId: z.string().uuid().nullable(),
  createdBy: z.number().int().positive(),
  status: zShipmentStatus,
  pickupDate: zDateOnlyString,
  pickupWindowStart: zTimeOnlyString,
  pickupWindowEnd: zTimeOnlyString,
  contactName: z.string(),
  contactEmail: z.string().email(),
  contactTelephoneNumber: z.string().nullable(),
  pickupCountry: z.string().nullable(),
  pickupAddressLine1: z.string().nullable(),
  pickupAddressLine2: z.string().nullable(),
  pickupAddressLine3: z.string().nullable(),
  pickupPostalCode: z.string().nullable(),
  pickupCity: z.string().nullable(),
  taxId: z.string().nullable(),
  packageWeightKg: z.number().nullable(),
  packageLengthCm: z.number().nullable(),
  packageWidthCm: z.number().nullable(),
  packageHeightCm: z.number().nullable(),
  confirmationUrl: zShipmentConfirmationUrl.nullable(),
  documents: z.array(zShipmentDocumentResponse),
  documentCentreVisible: z.boolean(),
  quotaSpentAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  items: z.array(zShipmentResponseItem),
  organisation: zShipmentResponseOrganisation,
  labWorkbook: zShipmentResponseLabWorkbook.nullable(),
  creator: zShipmentResponseCreator,
});
export type ShipmentResponse = z.infer<typeof zShipmentResponse>;

export const zCreateShipmentResponse = zShipmentResponse;
export type CreateShipmentResponse = z.infer<typeof zCreateShipmentResponse>;

export const zAdminShipmentResponse = zShipmentResponse;
export type AdminShipmentResponse = z.infer<typeof zAdminShipmentResponse>;

export const zShipmentDocumentEmailRequest = z.object({
  recipientEmail: z.string().trim().email(),
  subject: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(20_000),
});
export type ShipmentDocumentEmailRequest = z.infer<typeof zShipmentDocumentEmailRequest>;

export const zShipmentDocumentEmailResponse = z.object({
  recipientEmail: z.string().email(),
  emailDeliveryId: z.number().int().positive().nullable(),
  messageId: z.string().nullable(),
});
export type ShipmentDocumentEmailResponse = z.infer<typeof zShipmentDocumentEmailResponse>;

export const zAdminShipmentsQuery = z
  .object({
    includeReceived: zBooleanish.optional(),
    organisationId: z.coerce.number().int().positive().optional(),
    labWorkbookId: z.string().uuid().optional(),
    status: z.union([zShipmentStatus, z.array(zShipmentStatus)]).optional(),
    search: z.string().trim().optional(),
  })
  .transform(value => ({
    includeReceived: value.includeReceived ?? false,
    organisationId: value.organisationId,
    labWorkbookId: value.labWorkbookId,
    statuses:
      value.status === undefined
        ? ([] as ShipmentStatus[])
        : Array.isArray(value.status)
          ? value.status
          : [value.status],
    search: value.search && value.search.length > 0 ? value.search : undefined,
  }));
export type AdminShipmentsQuery = z.infer<typeof zAdminShipmentsQuery>;

export const zAdminShipmentsResponse = z.object({
  rows: z.array(zShipmentResponse),
  total: z.number().int().nonnegative(),
});
export type AdminShipmentsResponse = z.infer<typeof zAdminShipmentsResponse>;

export const zAdminOrganisationShipmentsQuery = z
  .object({
    includeReceived: zBooleanish.optional(),
  })
  .transform(value => ({
    includeReceived: value.includeReceived ?? false,
  }));
export type AdminOrganisationShipmentsQuery = z.infer<typeof zAdminOrganisationShipmentsQuery>;

export const zAdminOrganisationShipmentsResponse = z.object({
  shipments: z.array(zShipmentResponse),
});
export type AdminOrganisationShipmentsResponse = z.infer<typeof zAdminOrganisationShipmentsResponse>;

export const zShippingWorkbook = z
  .object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    sampleSheetName: z.string().nullable(),
    xlShipmentsAuthorised: z.boolean(),
    organisation: z.object({
      id: z.number().int().positive(),
      name: z.string(),
      country: z.string().nullable(),
      pickupAddressLine1: z.string().nullable(),
      pickupAddressLine2: z.string().nullable(),
      pickupAddressLine3: z.string().nullable(),
      pickupPostalCode: z.string().nullable(),
      pickupCity: z.string().nullable(),
      taxId: z.string().nullable(),
    }),
  })
  .extend(zShipmentQuotaSummary.shape);
export type ShippingWorkbook = z.infer<typeof zShippingWorkbook>;

export const zShippingWorkbooksResponse = z.object({
  workbooks: z.array(zShippingWorkbook),
});
export type ShippingWorkbooksResponse = z.infer<typeof zShippingWorkbooksResponse>;

export const zDeleteShipmentResponse = z.object({
  deleted: z.literal(true),
});
export type DeleteShipmentResponse = z.infer<typeof zDeleteShipmentResponse>;

export const zPatchLabWorkbookShippingSettingsRequest = z
  .object({
    shippingRequestQuota: z.number().int().min(0).max(1000).optional(),
    xlShipmentsAuthorised: z.boolean().optional(),
  })
  .refine(payload => Object.keys(payload).length > 0, 'At least one shipping setting is required.');
export type PatchLabWorkbookShippingSettingsRequest = z.infer<typeof zPatchLabWorkbookShippingSettingsRequest>;

export const zShippingNotificationSubscriber = z.object({
  id: z.number().int().positive(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
});
export type ShippingNotificationSubscriber = z.infer<typeof zShippingNotificationSubscriber>;

export const zShippingNotificationSettingsResponse = z.object({
  subscribed: z.boolean(),
  subscribers: z.array(zShippingNotificationSubscriber),
});
export type ShippingNotificationSettingsResponse = z.infer<typeof zShippingNotificationSettingsResponse>;

export const zPatchShippingNotificationSettingsRequest = z.object({
  subscribed: z.boolean(),
});
export type PatchShippingNotificationSettingsRequest = z.infer<typeof zPatchShippingNotificationSettingsRequest>;
